import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@app/components/ui/Toast/toast'
import { EvidenceDocument } from '../documents.types'
import { formatDate, formatFileSize, getDocumentType } from '../documents.utils'
import { downloadDocument } from '../service/documents.service'
import { useFetchDocumentPreviewUrl } from '../hooks/useDocuments'
import { PdfPreview } from './pdf-preview'
import { ImagePreview } from './image-preview'
import { GeoreferencePreviewMap } from './georeference-preview-map'
import { MarkdownRenderer } from './markdown-renderer'

type DocumentPreviewDialogProps = {
  document: EvidenceDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// SVG excluded — more XML attack surface than raster formats for user-uploaded content.
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp'])

// Client-side only — the preview endpoint has no size limit of its own.
const IMAGE_PREVIEW_MAX_BYTES = 10 * 1024 * 1024

const METADATA_ROWS: {
  label: string
  value: (doc: EvidenceDocument) => string
}[] = [
  { label: 'Owner', value: (doc) => doc.ownerEmail ?? 'Deleted user' },
  { label: 'Organisation', value: (doc) => doc.organisation ?? '—' },
  { label: 'Date published', value: (doc) => formatDate(doc.publishedAt) },
  { label: 'Date uploaded', value: (doc) => formatDate(doc.uploadedAt) },
  { label: 'File size', value: (doc) => formatFileSize(doc.file.sizeBytes) },
  {
    label: 'Permissions',
    value: (doc) => (doc.visibility === 'personal' ? 'Personal' : 'Organisation'),
  },
  { label: 'Source', value: (doc) => doc.source ?? '—' },
  {
    label: 'Geo ref',
    value: (doc) =>
      doc.georeference.type === 'NONE' ? '—' : (doc.georeference.searchLabel ?? 'Set'),
  },
]

export const DocumentPreviewDialog = ({
  document,
  open,
  onOpenChange,
}: DocumentPreviewDialogProps) => {
  const [previewFailed, setPreviewFailed] = useState(false)
  const documentType = document ? getDocumentType(document.file.originalFilename) : ''
  const isPdf = documentType === 'pdf'
  const isImage = IMAGE_EXTENSIONS.has(documentType)
  const imageTooLarge = isImage && (document?.file.sizeBytes ?? 0) > IMAGE_PREVIEW_MAX_BYTES
  const canPreview = isPdf || (isImage && !imageTooLarge)
  const previewUrlQuery = useFetchDocumentPreviewUrl(document?.id, open && canPreview)
  // isError covers hop 1 (resolving the presigned URL itself) failing; previewFailed covers
  // hop 2 (pdf.js/the <img> tag failing to load/render what that URL points to).
  const showPreview = canPreview && !previewFailed && !previewUrlQuery.isError

  // Reset per-document, not just on close — the dialog can swap straight from one document to
  // another (clicking a different row's title) without `open` ever toggling back to false.
  useEffect(() => setPreviewFailed(false), [document?.id])

  if (!document) return null

  const handleDownload = () =>
    downloadDocument(document.id, document.file.originalFilename).catch(() =>
      toast({ description: 'Failed to download document.', position: 'top-right', type: 'error' }),
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] p-0 flex overflow-hidden gap-0">
        <div className="flex-1 min-w-0 bg-slate-50 overflow-y-auto p-6">
          {showPreview && previewUrlQuery.data && isPdf && (
            <PdfPreview url={previewUrlQuery.data.url} onError={() => setPreviewFailed(true)} />
          )}

          {showPreview && previewUrlQuery.data && isImage && (
            <ImagePreview
              url={previewUrlQuery.data.url}
              alt={document.title}
              onError={() => setPreviewFailed(true)}
            />
          )}

          {showPreview && !previewUrlQuery.data && <Skeleton className="h-full w-full" />}

          {!showPreview && (
            <div className="h-full rounded-md border bg-white flex flex-col items-center justify-center gap-2">
              <FileText className="h-8 w-8 text-neutral-400" />
              <p className="text-sm text-neutral-500">
                {imageTooLarge
                  ? 'This image is too large to preview — download to view.'
                  : isPdf
                    ? "This PDF couldn't be previewed — download to view."
                    : isImage
                      ? "This image couldn't be previewed — download to view."
                      : 'Preview isn’t available for this format — download to view.'}
              </p>
            </div>
          )}
        </div>

        <div className="w-[420px] shrink-0 border-l flex flex-col overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b text-left space-y-1.5">
            <DialogTitle>{document.title}</DialogTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary" size="sm">
                {document.category}
              </Badge>
              <span>{getDocumentType(document.file.originalFilename).toUpperCase()}</span>
              <span>{formatFileSize(document.file.sizeBytes)}</span>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
            {document.descriptionMd && (
              <MarkdownRenderer markdown={document.descriptionMd} className="text-neutral-700" />
            )}

            <GeoreferencePreviewMap georeference={document.georeference} />

            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
              {METADATA_ROWS.map((row) => {
                const value = row.value(document)
                return (
                  <div className="contents" key={row.label}>
                    <dt className="text-neutral-500">{row.label}</dt>
                    <dd className="min-w-0 truncate" title={value}>
                      {value}
                    </dd>
                  </div>
                )
              })}
            </dl>

            {document.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {document.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 p-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={handleDownload}>Download</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
